import React, { useState, useEffect } from 'react';
import { Modal } from '../common/Modal/Modal';
import styles from './VragenModule.module.css';

interface EditQuestionModalProps {
  question: any;
  onClose: () => void;
  onSave: (id: string, question: any) => void;
  onDelete?: (id: string) => void;
}

export const EditQuestionModal: React.FC<EditQuestionModalProps> = ({ question, onClose, onSave, onDelete }) => {
  const [formData, setFormData] = useState({
    question: '',
    source: '',
    author: '',
    authorEmail: '',
    authorCategory: 'overig' as string,
    answer: '',
    answeredByName: '',
    status: 'open',
    tags: '',
    notes: '',
    publicVisible: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (question) {
      // Handle author field - could be a string or an object
      let authorName = '';
      if (typeof question.author === 'string') {
        authorName = question.author;
      } else if (question.author?.name) {
        authorName = question.author.name;
      } else if (question.askedBy?.name) {
        authorName = question.askedBy.name;
      }

      // Handle answer field - could be a string or an object
      let answerContent = '';
      if (typeof question.answer === 'string') {
        answerContent = question.answer;
      } else if (question.answer?.content) {
        answerContent = question.answer.content;
      }

      // Map status from backend format to frontend format
      let mappedStatus = question.status || 'open';
      if (mappedStatus === 'in_progress') mappedStatus = 'in_behandeling';
      else if (mappedStatus === 'answered') mappedStatus = 'beantwoord';
      else if (mappedStatus === 'closed') mappedStatus = 'gesloten';

      setFormData({
        question: question.question || '',
        source: question.channel || question.source || '',
        author: authorName,
        authorEmail: question.askedBy?.email || question.author?.email || '',
        authorCategory: question.askedBy?.category || 'overig',
        answer: answerContent,
        answeredByName: question.answer?.answeredByName || '',
        status: mappedStatus,
        tags: Array.isArray(question.tags) ? question.tags.join(', ') : '',
        notes: question.internalNotes?.[0]?.note || question.followUp?.notes || '',
        publicVisible: question.publicVisible || false
      });
    }
  }, [question]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.question.trim()) {
      newErrors.question = 'Vraag is verplicht';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validate()) {
      const tagsArray = formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
      onSave(question._id || question.id, {
        ...formData,
        tags: tagsArray,
        authorEmail: formData.authorEmail,
        authorCategory: formData.authorCategory,
        answeredByName: formData.answeredByName,
        updatedAt: new Date().toISOString()
      });
      onClose();
    }
  };

  const handleDelete = () => {
    if (window.confirm('Weet u zeker dat u deze vraag wilt verwijderen?')) {
      if (onDelete) {
        onDelete(question._id || question.id);
        onClose();
      }
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Vraag bewerken">
      <form onSubmit={handleSubmit} className={styles.modalForm}>
        <div className={styles.formSection}>
          <h3 className={styles.sectionTitle}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            Vraag details
          </h3>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              Vraag
              <span className={styles.required}>*</span>
            </label>
            <textarea
              name="question"
              value={formData.question}
              onChange={handleChange}
              className={`${styles.textarea} ${errors.question ? styles.error : ''}`}
              rows={3}
              placeholder="Stel hier de vraag..."
            />
            {errors.question && <span className={styles.errorText}>{errors.question}</span>}
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.7 }}>
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="8.5" cy="7" r="4" />
                  <polyline points="17 11 19 13 23 9" />
                </svg>
                Auteur
                <span className={styles.helpText}>Optioneel</span>
              </label>
              <input
                type="text"
                name="author"
                value={formData.author}
                onChange={handleChange}
                className={styles.input}
                placeholder="Naam van de vraagsteller"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>
                E-mailadres
                <span className={styles.helpText}>Optioneel</span>
              </label>
              <input
                type="email"
                name="authorEmail"
                value={formData.authorEmail}
                onChange={handleChange}
                className={styles.input}
                placeholder="email@voorbeeld.nl"
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Categorie afzender
              </label>
              <select
                name="authorCategory"
                value={formData.authorCategory}
                onChange={handleChange}
                className={styles.select}
              >
                <option value="bewoner">Bewoner</option>
                <option value="journalist">Journalist</option>
                <option value="stakeholder">Stakeholder</option>
                <option value="overheid">Overheid</option>
                <option value="ondernemer">Ondernemer</option>
                <option value="belangenorganisatie">Belangenorganisatie</option>
                <option value="overig">Overig</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.7 }}>
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                </svg>
                Bron / Kanaal
                <span className={styles.helpText}>Optioneel</span>
              </label>
              <select
                name="source"
                value={formData.source}
                onChange={handleChange}
                className={styles.select}
              >
                <option value="">Selecteer een bron...</option>
                <option value="Email">Email</option>
                <option value="Telefoon">Telefoon</option>
                <option value="Website">Website</option>
                <option value="Social Media">Social Media</option>
                <option value="Bijeenkomst">Bijeenkomst</option>
                <option value="Klachtenformulier">Klachtenformulier</option>
                <option value="Informeel gesprek">Informeel gesprek</option>
                <option value="Brief">Brief</option>
              </select>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.7 }}>
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                <line x1="7" y1="7" x2="7.01" y2="7" />
              </svg>
              Tags
              <span className={styles.helpText}>Gescheiden door komma's</span>
            </label>
            <input
              type="text"
              name="tags"
              value={formData.tags}
              onChange={handleChange}
              className={styles.input}
              placeholder="bijv. urgent, bewoners, fase 2"
            />
          </div>
        </div>

        <div className={styles.formSection}>
          <h3 className={styles.sectionTitle}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
            </svg>
            Antwoord & Status
          </h3>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              Antwoord
              <span className={styles.helpText}>Optioneel</span>
            </label>
            <textarea
              name="answer"
              value={formData.answer}
              onChange={handleChange}
              className={styles.textarea}
              rows={4}
              placeholder="Geef hier het antwoord op de vraag..."
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              Beantwoord door
              <span className={styles.helpText}>Naam van de beantwoorder</span>
            </label>
            <input
              type="text"
              name="answeredByName"
              value={formData.answeredByName}
              onChange={handleChange}
              className={styles.input}
              placeholder="Naam van degene die het antwoord heeft gegeven"
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.7 }}>
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className={styles.select}
            >
              <option value="open">Open</option>
              <option value="in_behandeling">In behandeling</option>
              <option value="beantwoord">Beantwoord</option>
              <option value="gesloten">Gesloten</option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.publicVisible}
                onChange={(e) => setFormData(prev => ({ ...prev, publicVisible: e.target.checked }))}
                style={{ accentColor: '#4CAF50' }}
              />
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.7 }}>
                <circle cx="12" cy="12" r="10" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
              Tonen op projectwebsite (FAQ)
            </label>
            <span className={styles.helpText} style={{ fontSize: '0.8rem', color: '#9DA6A8' }}>
              Vraag en antwoord worden zichtbaar in de FAQ-sectie van de publieke projectwebsite
            </span>
          </div>
        </div>

        <div className={styles.formSection}>
          <div className={styles.formGroup}>
            <label className={styles.label}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.7 }}>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
              Interne notities
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className={styles.textarea}
              rows={3}
              placeholder="Interne opmerkingen of notities..."
            />
          </div>
        </div>

        <div className={styles.modalActions}>
          <div className={styles.modalActionsLeft}>
            {onDelete && (
              <button type="button" onClick={handleDelete} className={styles.btnDanger}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  <line x1="10" y1="11" x2="10" y2="17" />
                  <line x1="14" y1="11" x2="14" y2="17" />
                </svg>
                Verwijderen
              </button>
            )}
          </div>
          <div className={styles.modalActionsRight}>
            <button type="button" onClick={onClose} className={styles.btnSecondary}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
              Annuleren
            </button>
            <button type="submit" className={styles.btnPrimary}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                <polyline points="17 21 17 13 7 13 7 21" />
                <polyline points="7 3 7 8 15 8" />
              </svg>
              Wijzigingen opslaan
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};